import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { useRoute, navigate } from "../../lib/router";
import { AREA_LABEL, type Area } from "../../lib/refdata";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { Panel } from "../../components/ui";
import { AddPerson } from "./People";

/** /add — a contact shared from the phone (iPhone Shortcut "Add to CRM"), or typed in. Pick the list, check, save. */
const LISTS: { area: Area; label: string; hint: string }[] = [
  { area: "new_connections", label: "New connection", hint: "Online, DMs, someone new" },
  { area: "networking", label: "Met in person / at an event", hint: "48-hour follow-up starts" },
  { area: "hnw", label: "High net worth", hint: "Business owners, professionals" },
  { area: "warm", label: "Warm market", hint: "People you already know" },
  { area: "travel", label: "Travel", hint: "Travel insurance" },
];

/** The Shortcut sends labelled lines ("First: Ada\nLast: Okafor\nPhone: …"); plain query params work too. */
function readShared(q: URLSearchParams): Record<string, string> {
  const out: Record<string, string> = {};
  const map: Record<string, string> = { first: "first_name", last: "last_name", phone: "phone", email: "email", company: "company", job: "occupation", note: "notes", notes: "notes" };
  const c = q.get("c");
  if (c) {
    let key = "";
    for (const line of c.split(/\r?\n/)) {
      const m = /^\s*(first|last|phone|email|company|job|notes?)\s*:\s*(.*)$/i.exec(line);
      if (m) { key = map[m[1].toLowerCase()]; if (m[2].trim() && !out[key]) out[key] = m[2].trim(); }
      else if (key && line.trim() && !out[key]) out[key] = line.trim();   // a value on the next line
    }
  }
  for (const [k, f] of Object.entries(map)) { const v = q.get(k); if (v && !out[f]) out[f] = v.trim(); }
  const name = q.get("name");
  if (name && !out.first_name) { const [a, ...b] = name.trim().split(/\s+/); out.first_name = a; out.last_name = b.join(" "); }
  return out;
}

export default function QuickAdd() {
  const { query } = useRoute();
  const { can } = useSession();
  const shared = useMemo(() => readShared(query), [query]);
  const has = !!(shared.first_name || shared.phone || shared.email);
  const [area, setArea] = useState<Area | null>((query.get("list") as Area) || null);
  const [done, setDone] = useState(false);
  const who = [shared.first_name, shared.last_name].filter(Boolean).join(" ") || shared.phone || shared.email;

  if (!can("leads", "edit")) return <><PageHead title="Add a contact" /><Panel><div className="panel-body">You need permission to add people.</div></Panel></>;
  if (done) return (
    <><PageHead title="Saved" sub={`${who || "The contact"} is in the CRM.`} />
      <div className="qa-done">
        <button className="btn primary" onClick={() => navigate("/leads")}>Go to Leads today</button>
        <button className="btn" onClick={() => { setDone(false); setArea(null); navigate("/add"); }}>Add another</button>
        <p className="small muted">On iPhone you can now swipe back to Contacts.</p>
      </div></>);

  return (
    <>
      <PageHead title={has ? `Add ${who}` : "Add a contact"} sub={has ? "Which list does this person belong to?" : "Pick a list, then type their details."} />
      <div className="qa-lists">
        {LISTS.map((l) => (
          <button key={l.area} className="qa-list" onClick={() => setArea(l.area)}>
            <b>{l.label}</b><span className="small muted">{l.hint}</span>
          </button>))}
      </div>
      {!has && <ShortcutHelp />}
      {area && <AddPerson area={area} outreach={false}
        initial={{ ...shared, ...(area === "networking" ? { date_met: dayjs().format("YYYY-MM-DD") } : {}), source: shared.source ?? "Phone contact" }}
        onClose={() => setArea(null)} onSaved={() => { setArea(null); setDone(true); }} />}
      <p className="small muted" style={{ marginTop: 12 }}>Adds to: {area ? AREA_LABEL[area] : "pick a list above"}.</p>
    </>
  );
}

function ShortcutHelp() {
  return (
    <p className="small muted mt16">Saving contacts on your phone? Give them a CRM label or a #tag and they come in by themselves: <a href="/settings/phone-contacts">Settings → Phone contacts</a>.</p>
  );
}
